import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Success, Error } from "ui-components/Alert";
import { formRender, initialValues, stockHoldValidation } from "./stock-hold-schema";
import {
  formRender as detailRender,
  initialValues as detailValues,
  stockHoldDetailValidation,
} from "./stock-hold-detail-schema";
import StockHoldInfo from "./components/StockHoldInfo";
import { inventoryColumns } from "./components/columns";
import MDTypography from "components/MDTypography";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { useCustomerValue } from "providers/CustomerContext";
import { createStockHold } from "services/data.internal.service";
import StockHoldDetailInfo from "./components/StockHoldDetailInfo";
import MDButton from "components/MDButton";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import dayjs from "dayjs";
import { Fieldset } from "ui-components/Form";
import { InboundLayout } from "ui-components/Layout";
import { Card, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { GridCellEditStartReasons } from "@mui/x-data-grid";
import { getCommonInventoryList } from "services/data.inventory.service";
import { DeleteButton } from "ui-components/IconButton";
import { StockHoldCaption } from "layouts/views/inventory/components/Caption";
import { LoadingDialog } from "ui-components/Alert";
import { LoadingAlert } from "ui-components/Alert";
import { Error_ } from "ui-components/Alert";

const defaultDetailValues = {
  ...detailValues,
  holdPallet: { label: "All pallet", value: "A" },
};

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".custom-editable-header": {
    backgroundColor: "#1A73E8 !important",
    color: "#fff",
    fontWeight: "bold",
    whiteSpace: "normal !important",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
  ".highlight": {
    backgroundColor: "pink !important",
  },
});

const isCellEditable = (params) => {
  if (params.field === "holdQuantity") {
    return params.row.quantityAvailable > 0;
  }
  return false;
};

function AddStockHold() {
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { customerCode } = useCustomerValue();
  const errorColumns = [
    {
      field: "id",
      headerName: "LocAvaiID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locationID",
      headerName: "Location ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "skuCode",
      headerName: "SKU Code",
      sortable: false,
      align: "left",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "palletNbr",
      headerName: "Pallet Nbr",
      sortable: false,
      align: "left",
      flex: 1.2,
      minWidth: 112,
    },
    {
      field: "cartonID",
      headerName: "Carton ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "mln",
      headerName: "MLN",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "message",
      headerName: "Message",
      sortable: false,
      align: "left",
      flex: 1.6,
      minWidth: 160,
    },
  ];

  const [detail, setDetail] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loadEnabled, setLoadEnabled] = useState(false);

  const [errorItems, setErrorItems] = useState([]);

  const createStockHoldMutation = useMutation(createStockHold, {
    onSuccess: (data) => {
      setDialog(null);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const errorData = data.response.data.data;
      if (errorData) {
        setDialog(
          <Dialog open={true} maxWidth={"lg"}>
            <DialogTitle>{data.response.data.message}</DialogTitle>
            <DialogContent>
              <EditableStyledDataGrid
                columns={errorColumns}
                rows={errorData}
                sx={{
                  borderRadius: "0.75rem",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                getRowHeight={() => "auto"}
                hideFooterSelectedRowCount={true}
              />
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "center" }}>
              <MDButton
                color="info"
                style={{ width: "4rem" }}
                onClick={() => {
                  setDialog(null);
                }}
              >
                Ok
              </MDButton>
            </DialogActions>
          </Dialog>
        );
        const remainingDetail = detail.filter((row) => !errorData.some((err) => err.id === row.id));
        setDetail(remainingDetail);

        setErrorItems(errorData);
      }
      setLoadEnabled(true);
      setAlert(<Error data={data} onChange={setAlert} />);
    },
  });

  const defaultValues = {
    ...initialValues,
    customerCode,
  };

  const form = useForm({
    defaultValues: defaultValues,
    values: defaultValues,
    resolver: yupResolver(stockHoldValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
    setValue,
  } = form;

  const [warehouseCode] = watch(["warehouseCode"]);

  useEffect(() => {
    setDetail([]);
    setInventory([]);
  }, [warehouseCode]);

  const infoProps = {
    control,
    errors,
    formRender,
    customerCode,
    setValue,
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    setDialog(<LoadingDialog />)

    const { ...data } = getValues();

    const req = {
      ...data,
      type: "HOLD",
      stockHoldDetails: detail.map((x) => {
        const { expiryDate, ...data } = x;
        return {
          expiryDate: expiryDate ? dayjs(expiryDate, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
    };

    createStockHoldMutation.mutate(req, {
      onSuccess: () => {
        reset();
      },
    });
  };
  const createStatus = createStockHoldMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <StockHoldInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <StockHoldDetail
        customerCode={customerCode}
        warehouseCode={warehouseCode}
        inventory={inventory}
        setInventory={setInventory}
        detail={detail}
        setDetail={setDetail}
        loadEnabled={loadEnabled}
        setLoadEnabled={setLoadEnabled}
        errorItems={errorItems}
        setErrorItems={setErrorItems}
      />
      <InboundSaveButton
        label="submit"
        disabled={createStatus || detail.length === 0}
        url="/inventory/stock-hold"
        onClick={onSubmit}
      />
    </InboundLayout>
  );
}

const StockHoldDetail = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
}) => {
  const [alert, setAlert] = useState(null);

  const handleProcessRowUpdateError = (error) => {
    setAlert(<Error content={error.message} onChange={setAlert} />);
  };

  const onCellEditStart = (params) => {
    if (params.value == null) {
      if (params.field === "holdQuantity") {
        if (params.reason === GridCellEditStartReasons.cellDoubleClick) {
          const updatedInventory = inventory.map((row) => {
            if (row.id === params.row.id) {
              params.row.holdQuantity = row.quantityAvailable;
              return { ...row, holdQuantity: row.quantityAvailable };
            }
            return row;
          });
          setInventory(updatedInventory);
        }
      }
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <InventoryGrid
          customerCode={customerCode}
          warehouseCode={warehouseCode}
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
          onCellEditStart={onCellEditStart}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          loadEnabled={loadEnabled}
          setLoadEnabled={setLoadEnabled}
          errorItems={errorItems}
          setErrorItems={setErrorItems}
        />
        {alert}
        <PreviewGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
        />
      </MDBox>
    </Card>
  );
};

const InventoryGrid = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  setAlert,
  onCellEditStart,
  handleProcessRowUpdateError,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
}) => {
  const [formValues, setFormValues] = useState({ ...defaultDetailValues });
  const [selectedIds, setSelectedIds] = useState([]);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(stockHoldDetailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    reset();
  }, [warehouseCode]);

  const [zoneID, locationID, vendorCode, skuCode, cartonID, palletNbr, holdPallet] = watch([
    "zoneID",
    "locationID",
    "vendorCode",
    "skuCode",
    "cartonID",
    "palletNbr",
    "holdPallet",
  ]);

  const detailProps = {
    control,
    errors,
    formRender: detailRender,
    watch,
    warehouseCode,
  };

  const inventoryReq = {
    warehouseCode,
    customerCode,
    zoneID,
    locationID,
    vendorCode,
    skuCode,
    palletNbr,
    cartonID,
    holdPallet: holdPallet.value,
    type: "StockHold",
  };

  const { isFetching } = useQuery({
    queryKey: [
      "stock-hold-inventory-list",
      warehouseCode,
      customerCode,
      zoneID,
      locationID,
      vendorCode,
      skuCode,
      palletNbr,
      cartonID,
      holdPallet,
      loadEnabled,
    ],
    queryFn: () => getCommonInventoryList(inventoryReq),
    enabled: loadEnabled,
    onSuccess: (data) => {
      const list = data.data.data;
      if (list.length === 0) {
        setAlert(
          <Error
            content="No pallets found, might be occupied by Physical Count CCFreeze, Replenishment, Putaway"
            onChange={setAlert}
          />
        );
        setInventory([]);
      } else {
        const inventoryList = list
          .map((x) => ({
            ...x,
            holdQuantity: null,
            finalQuantity: x.quantityAvailable,
          }))
          .filter((x) => !detail.find((y) => x.id === y.id));
        setInventory(inventoryList);
      }
      setLoadEnabled(false);
    },
  });

  const onSubmit = () => {
    setLoadEnabled(true);
  };

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (JSON.stringify(updatedRow) !== JSON.stringify(originalRow)) {
      if (!selectedIds.includes(updatedRow.id)) {
        setSelectedIds([...selectedIds, updatedRow.id]);
      }
      const newInventory = inventory.map((inv) => {
        if (inv.id === updatedRow.id) {
          if (updatedRow.holdQuantity !== null) {
            if (updatedRow.holdQuantity > 0) {
              const finalQty =
                parseInt(updatedRow.quantityAvailable) - parseInt(updatedRow.holdQuantity);
              if (finalQty >= 0) {
                originalRow.holdQuantity = updatedRow.holdQuantity;
                originalRow.finalQuantity = finalQty;
                inv.holdQuantity = updatedRow.holdQuantity;
                inv.finalQuantity = finalQty;
              } else {
                originalRow.holdQuantity = originalRow.quantityAvailable;
                originalRow.finalQuantity = 0;
                inv.holdQuantity = originalRow.quantityAvailable;
                inv.finalQuantity = 0;
              }
            } else {
              originalRow.holdQuantity = null;
              originalRow.finalQuantity = originalRow.quantityAvailable;
              inv.holdQuantity = null;
              inv.finalQuantity = originalRow.quantityAvailable;
            }
          } else {
            originalRow.holdQuantity = null;
            originalRow.finalQuantity = inv.quantityAvailable;
            inv.holdQuantity = null;
            inv.finalQuantity = inv.quantityAvailable;
          }
        }
        return inv;
      });
      setInventory(newInventory);
    }
    return originalRow;
  };

  const saveDetail = () => {
    const newDetail = inventory.filter((x) => x.holdQuantity !== null && x.holdQuantity != 0);
    const unsuccessfulDetail = inventory.filter((x) =>
      selectedIds.some(y => y === x.id) && !newDetail.some(y => y.id === x.id)).map(x => {
        return x.skuCode + ': No Hold Quantity'
      });

    if (unsuccessfulDetail.length > 0) {
      const errorMsg = (
        <>
          {unsuccessfulDetail.map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </>
      );
      setAlert(<Error_ content={errorMsg} onChange={setAlert} />);
      if (newDetail.length === 0) {
        return;
      }
    }
    if (detail.length > 0) {
      let modifiedDetail = JSON.parse(JSON.stringify(detail));

      const filteredInv = newDetail.filter((x) => !detail.some((y) => y.id === x.id));

      modifiedDetail = [...modifiedDetail, ...filteredInv];

      setDetail(modifiedDetail);
    } else {
      setDetail(newDetail);
    }

    setErrorItems([]);

    const leftInventory = inventory.filter((x) => x.holdQuantity === null || x.holdQuantity === 0);
    if (leftInventory.length < inventory.length) {
      setInventory(leftInventory);
    }
  };
  const selectRow = (id) => {
    if (selectedIds.length > id.length) {
      const removed = selectedIds.filter((x) => !id.includes(x));
      let updatedRow = inventory;
      removed.forEach((removedID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === removedID) {
            return {
              ...x,
              holdQuantity: null,
              finalQuantity: x.quantityAvailable,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    }
    setSelectedIds(id);
  };

  const onCellEditEnd = (params) => {
    if (
      params.row.quantity - params.row.quantityOnHold ??
      0 - (params.row.holdQuantity ?? 0) !== params.row.finalQuantity
    ) {
      const updatedInventory = inventory.map((row) => {
        if (row.id === params.row.id) {
          params.row.finalQuantity = row.quantityAvailable - (params.row.holdQuantity ?? 0);
          return { ...row, finalQuantity: params.row.finalQuantity };
        }
        return row;
      });
      if (!selectedIds.some((x) => x === params.row.id)) {
        setSelectedIds([...selectedIds, params.row.id]);
      }
      setInventory(updatedInventory);
    }
  };

  return (
    <Fieldset label={"Stock Hold Detail"}>
      {(isFetching) && <LoadingAlert />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <StockHoldDetailInfo {...detailProps} />
        <MDBox mt={2} width="100%" display="flex" justifyContent="center">
          <MDButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={customerCode === null || warehouseCode === null}
          >
            Load
          </MDButton>
        </MDBox>
      </form>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Inventory Datagrid Display
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={inventoryColumns}
          rows={inventory}
          checkboxSelection={true}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={selectRow}
          rowSelectionModel={selectedIds}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          isCellEditable={isCellEditable}
          onCellEditStart={onCellEditStart}
          onCellEditStop={onCellEditEnd}
          getRowClassName={(params) => {
            return errorItems.some((x) => x.id === params.row.id) ? "highlight" : "";
          }}
        />
      </div>

      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={saveDetail}>
          Add
        </MDButton>
      </MDBox>
    </Fieldset>
  );
};

const PreviewGrid = ({
  inventory,
  setInventory,
  detail,
  setDetail,
  handleProcessRowUpdateError,
}) => {
  const addBackInventory = (req) => {
    const { holdQuantity, finalQuantity, ...data } = req;
    const updatedInventory = [
      ...inventory,
      { holdQuantity: null, finalQuantity: req.quantityAvailable, ...data },
    ];
    setInventory(updatedInventory);
  };

  const handleDeleteClick = (req) => {
    addBackInventory(req);
    setDetail(detail.filter((row) => !(row.id === req.id)));
  };
  const previewColumn = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <DeleteButton
            title="Delete"
            onClick={() => {
              handleDeleteClick(row);
            }}
          />,
        ];
      },
    },
    ...inventoryColumns,
  ];

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (!(updatedRow.holdQuantity && updatedRow.holdQuantity > 0)) {
      handleDeleteClick(updatedRow);
      return { ...originalRow, _action: "delete" };
    } else {
      const newDetail = detail.map((det) => {
        if (det.id === updatedRow.id) {
          if (updatedRow.holdQuantity !== null) {
            if (updatedRow.holdQuantity > 0) {
              const finalQty =
                parseInt(updatedRow.quantityAvailable) - parseInt(updatedRow.holdQuantity);
              if (finalQty >= 0) {
                originalRow.holdQuantity = updatedRow.holdQuantity;
                originalRow.finalQuantity = finalQty;
                det.holdQuantity = updatedRow.holdQuantity;
                det.finalQuantity = finalQty;
              } else {
                originalRow.holdQuantity = originalRow.quantityAvailable;
                originalRow.finalQuantity = 0;
                det.holdQuantity = originalRow.quantityAvailable;
                det.finalQuantity = 0;
              }
            }
          }
        }
        return det;
      });
      setDetail(newDetail);
    }
    return originalRow;
  };

  const caption = () => {
    const totalHold =
      detail.length > 0
        ? detail.reduce((total, det) => {
          return total + det.holdQuantity;
        }, 0)
        : 0;
    return <StockHoldCaption totalRows={detail.length} totalHold={totalHold} />;
  };

  return (
    <MDBox p={3}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Stock Hold Detail Preview
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={previewColumn}
          rows={detail}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          isCellEditable={isCellEditable}
          slots={{
            footer: caption,
          }}
        />
      </div>
    </MDBox>
  );
};

export default AddStockHold;
